import type { FloorPlan, Hotspot, Preset } from '../../api/types.js';
import type { FloorplanPosition, FloorplanSnapshot } from '../../floorplan/types.js';
import type { HotspotScreenPosition, HotspotsSnapshot } from '../../hotspots/types.js';
import type {
  MeasurementProjection,
  MeasurementsSnapshot,
  MeasurementUnits,
} from '../../measure/types.js';
import type { TourSnapshot } from '../../tour/types.js';
import type { WalkSnapshot } from '../../walk/types.js';
import type { XRSnapshot } from '../../xr/types.js';
import type { ComponentInstance } from '../component.js';
import { setText } from '../dom-helpers.js';
import { mountControlHints } from './control-hints.js';
import { type FloorplanMinimapCallbacks, mountFloorplanMinimap } from './floorplan-minimap.js';
import { type HotspotDotsCallbacks, mountHotspotDots } from './hotspot-dots.js';
import { type HotspotPopupCallbacks, mountHotspotPopup } from './hotspot-popup.js';
import { type HotspotStripCallbacks, mountHotspotStrip } from './hotspot-strip.js';
import { mountMeasurementOverlay } from './measurement-overlay.js';
import { type MeasurementPanelCallbacks, mountMeasurementPanel } from './measurement-panel.js';
import { mountPerfHud } from './perf-hud.js';
import { mountPresetPill, type PresetPillCallbacks } from './preset-pill.js';
import { mountToolbar, type ToolbarCallbacks } from './toolbar.js';
import { mountTourStatus } from './tour-status.js';
import { mountWalkHud } from './walk-hud.js';
import { mountXRModal, type XRModalCallbacks } from './xr-modal.js';

export interface LoadedProps {
  filename: string;
  embed: boolean;
  showPerfHud: boolean;
  fps: number;
  presets: readonly Preset[];
  tourSnapshot: TourSnapshot;
  hotspots: readonly Hotspot[];
  hotspotsSnapshot: HotspotsSnapshot;
  hotspotPositions: readonly HotspotScreenPosition[];
  viewport: { width: number; height: number };
  // 4.H — floorplan can be null when the model has no plan attached.
  floorplan: FloorPlan | null;
  floorplanSnapshot: FloorplanSnapshot;
  floorplanPosition: FloorplanPosition | null;
  // 4.J — measurements (overlay + panel + raycast capability).
  measurementsSnapshot: MeasurementsSnapshot;
  measurementProjections: readonly MeasurementProjection[];
  measurementDistances: readonly number[];
  raycastAvailable: boolean;
  // 4.I1 — walk mode (FPV).
  walkSnapshot: WalkSnapshot;
  // 4.K — WebXR (VR / AR) modal + session state.
  xrSnapshot: XRSnapshot;
  // 4.M — pointer modality (coarse touch vs fine mouse). Drives
  // toolbar walk-disabled + control-hints content + (via body data
  // attribute set by bootstrap) tap-target sizing.
  pointerCoarse: boolean;
}

// 4.H: floorplan callbacks renamed to avoid clashing with hotspot-popup's
// generic `onClose`. The intersection below pins all UI surfaces.
interface FloorplanLoadedCallbacks {
  onFloorplanClose: () => void;
  onFloorplanTeleport: (pixelXY: [number, number]) => void;
  onFloorplanImageError: () => void;
}

// 4.J: measurement-panel callbacks. onMeasureRemoveLast is wired but
// unused by the panel UI in MVP (Backspace shortcut covers the path);
// kept on the contract for parity with future toolbar UI in 4.M.
interface MeasurementLoadedCallbacks {
  onMeasureReset: () => void;
  onMeasureSetUnits: (units: MeasurementUnits) => void;
  onMeasureRemoveLast: () => void;
}

// 4.K: XR modal callbacks. onXRClose triggered by the modal close
// button (and the orchestrator's ESC chain). onEnterVR / onEnterAR
// from the action buttons.
interface XRLoadedCallbacks {
  onXRClose: () => void;
  onEnterVR: () => void;
  onEnterAR: () => void;
}

export type LoadedCallbacks = ToolbarCallbacks &
  PresetPillCallbacks &
  HotspotDotsCallbacks &
  Omit<HotspotPopupCallbacks, 'onClose'> &
  Pick<HotspotPopupCallbacks, 'onClose'> &
  Pick<HotspotStripCallbacks, 'onHotspotOpen'> &
  FloorplanLoadedCallbacks &
  MeasurementLoadedCallbacks &
  XRLoadedCallbacks;

interface SubInstances {
  toolbar: ComponentInstance<{
    embed: boolean;
    pointerCoarse: boolean;
    xrStatus: XRSnapshot['status'];
  }>;
  perfHud: ComponentInstance<{ visible: boolean; fps: number; embed: boolean }>;
  hints: ComponentInstance<{ embed: boolean; pointerCoarse: boolean }>;
  presetPill: ComponentInstance<{
    presets: readonly Preset[];
    tourSnapshot: TourSnapshot;
    embed: boolean;
  }>;
  tourStatus: ComponentInstance<{ tourSnapshot: TourSnapshot }>;
  hotspotDots: ComponentInstance<{
    hotspots: readonly Hotspot[];
    positions: readonly HotspotScreenPosition[];
    snapshot: HotspotsSnapshot;
    measureActive: boolean;
  }>;
  hotspotStrip: ComponentInstance<{
    hotspots: readonly Hotspot[];
    snapshot: HotspotsSnapshot;
    embed: boolean;
  }>;
  hotspotPopup: ComponentInstance<{
    hotspots: readonly Hotspot[];
    snapshot: HotspotsSnapshot;
    activePosition: HotspotScreenPosition | undefined;
    viewport: { width: number; height: number };
  }>;
  floorplanMinimap: ComponentInstance<{
    floorplan: FloorPlan;
    snapshot: FloorplanSnapshot;
    position: FloorplanPosition | null;
    embed: boolean;
  }> | null;
  measurementOverlay: ComponentInstance<{
    snapshot: MeasurementsSnapshot;
    projections: readonly MeasurementProjection[];
    distances: readonly number[];
  }>;
  measurementPanel: ComponentInstance<{
    snapshot: MeasurementsSnapshot;
    distances: readonly number[];
    raycastAvailable: boolean;
    embed: boolean;
  }>;
  walkHud: ComponentInstance<{
    snapshot: WalkSnapshot;
    embed: boolean;
  }>;
  xrModal: ComponentInstance<{
    snapshot: XRSnapshot;
    embed: boolean;
  }>;
}

export function mountLoaded(
  root: HTMLElement,
  initial: LoadedProps,
  callbacks: LoadedCallbacks,
): ComponentInstance<LoadedProps> {
  const wrap = document.createElement('div');
  wrap.style.cssText = 'position:absolute;inset:0;pointer-events:none';
  root.appendChild(wrap);

  const toast = document.createElement('div');
  toast.classList.add('viewer-ui-interactive');
  toast.setAttribute('role', 'status');
  toast.setAttribute('aria-live', 'polite');
  toast.style.cssText =
    'position:fixed;top:1rem;left:1rem;background:rgba(15,15,17,0.85);backdrop-filter:blur(8px);color:#f4f4f5;padding:0.5rem 0.75rem;border-radius:0.375rem;font-family:system-ui,sans-serif;font-size:0.75rem;z-index:11;opacity:1;transition:opacity 600ms ease 1500ms;pointer-events:none';
  setText(toast, `${initial.filename} caricato`);
  wrap.appendChild(toast);
  const toastFade = setTimeout(() => {
    toast.style.opacity = '0';
  }, 50);

  const subs: SubInstances = {
    toolbar: mountToolbar(
      wrap,
      {
        embed: initial.embed,
        pointerCoarse: initial.pointerCoarse,
        xrStatus: initial.xrSnapshot.status,
      },
      callbacks,
    ),
    perfHud: mountPerfHud(wrap, {
      visible: initial.showPerfHud,
      fps: initial.fps,
      embed: initial.embed,
    }),
    hints: mountControlHints(wrap, {
      embed: initial.embed,
      pointerCoarse: initial.pointerCoarse,
    }),
    presetPill: mountPresetPill(
      wrap,
      {
        presets: initial.presets,
        tourSnapshot: initial.tourSnapshot,
        embed: initial.embed,
      },
      callbacks,
    ),
    tourStatus: mountTourStatus(wrap, { tourSnapshot: initial.tourSnapshot }),
    // Hotspot UI: mount in stack order so popup z-index (12) sits above
    // dots/strip (11) on top of canvas (z-0).
    hotspotDots: mountHotspotDots(
      wrap,
      {
        hotspots: initial.hotspots,
        positions: initial.hotspotPositions,
        snapshot: initial.hotspotsSnapshot,
        measureActive: initial.measurementsSnapshot.active,
      },
      { onHotspotOpen: callbacks.onHotspotOpen },
    ),
    hotspotStrip: mountHotspotStrip(
      wrap,
      {
        hotspots: initial.hotspots,
        snapshot: initial.hotspotsSnapshot,
        embed: initial.embed,
      },
      { onHotspotOpen: callbacks.onHotspotOpen },
    ),
    hotspotPopup: mountHotspotPopup(
      wrap,
      {
        hotspots: initial.hotspots,
        snapshot: initial.hotspotsSnapshot,
        activePosition:
          initial.hotspotsSnapshot.openIndex >= 0
            ? initial.hotspotPositions[initial.hotspotsSnapshot.openIndex]
            : undefined,
        viewport: initial.viewport,
      },
      {
        onClose: callbacks.onClose,
        onPrev: callbacks.onPrev,
        onNext: callbacks.onNext,
      },
    ),
    floorplanMinimap:
      initial.floorplan === null
        ? null
        : mountFloorplanMinimap(
            wrap,
            {
              floorplan: initial.floorplan,
              snapshot: initial.floorplanSnapshot,
              position: initial.floorplanPosition,
              embed: initial.embed,
            },
            {
              onClose: callbacks.onFloorplanClose,
              onTeleport: callbacks.onFloorplanTeleport,
              onImageError: callbacks.onFloorplanImageError,
            } satisfies FloorplanMinimapCallbacks,
          ),
    measurementOverlay: mountMeasurementOverlay(wrap, {
      snapshot: initial.measurementsSnapshot,
      projections: initial.measurementProjections,
      distances: initial.measurementDistances,
    }),
    measurementPanel: mountMeasurementPanel(
      wrap,
      {
        snapshot: initial.measurementsSnapshot,
        distances: initial.measurementDistances,
        raycastAvailable: initial.raycastAvailable,
        embed: initial.embed,
      },
      {
        onReset: callbacks.onMeasureReset,
        onSetUnits: callbacks.onMeasureSetUnits,
        onRemoveLast: callbacks.onMeasureRemoveLast,
      } satisfies MeasurementPanelCallbacks,
    ),
    walkHud: mountWalkHud(wrap, { snapshot: initial.walkSnapshot, embed: initial.embed }),
    xrModal: mountXRModal(wrap, { snapshot: initial.xrSnapshot, embed: initial.embed }, {
      onClose: callbacks.onXRClose,
      onEnterVR: callbacks.onEnterVR,
      onEnterAR: callbacks.onEnterAR,
    } satisfies XRModalCallbacks),
  };

  const update = (props: LoadedProps): void => {
    setText(toast, `${props.filename} caricato`);
    subs.perfHud.update({
      visible: props.showPerfHud,
      fps: props.fps,
      embed: props.embed,
    });
    subs.presetPill.update({
      presets: props.presets,
      tourSnapshot: props.tourSnapshot,
      embed: props.embed,
    });
    subs.tourStatus.update({ tourSnapshot: props.tourSnapshot });
    subs.hotspotDots.update({
      hotspots: props.hotspots,
      positions: props.hotspotPositions,
      snapshot: props.hotspotsSnapshot,
      measureActive: props.measurementsSnapshot.active,
    });
    subs.hotspotStrip.update({
      hotspots: props.hotspots,
      snapshot: props.hotspotsSnapshot,
      embed: props.embed,
    });
    subs.hotspotPopup.update({
      hotspots: props.hotspots,
      snapshot: props.hotspotsSnapshot,
      activePosition:
        props.hotspotsSnapshot.openIndex >= 0
          ? props.hotspotPositions[props.hotspotsSnapshot.openIndex]
          : undefined,
      viewport: props.viewport,
    });
    if (subs.floorplanMinimap !== null && props.floorplan !== null) {
      subs.floorplanMinimap.update({
        floorplan: props.floorplan,
        snapshot: props.floorplanSnapshot,
        position: props.floorplanPosition,
        embed: props.embed,
      });
    }
    subs.measurementOverlay.update({
      snapshot: props.measurementsSnapshot,
      projections: props.measurementProjections,
      distances: props.measurementDistances,
    });
    subs.measurementPanel.update({
      snapshot: props.measurementsSnapshot,
      distances: props.measurementDistances,
      raycastAvailable: props.raycastAvailable,
      embed: props.embed,
    });
    subs.walkHud.update({ snapshot: props.walkSnapshot, embed: props.embed });
    subs.xrModal.update({ snapshot: props.xrSnapshot, embed: props.embed });
    // 4.M: propagate live pointer/hint changes to the toolbar +
    // hints components so Surface 2-in-1 keyboard plug/unplug
    // toggles walk-disabled and the touch/keyboard hint roster
    // without remount.
    subs.toolbar.update({
      embed: props.embed,
      pointerCoarse: props.pointerCoarse,
      xrStatus: props.xrSnapshot.status,
    });
    subs.hints.update({ embed: props.embed, pointerCoarse: props.pointerCoarse });
  };

  return {
    update,
    cleanup() {
      clearTimeout(toastFade);
      subs.toolbar.cleanup();
      subs.perfHud.cleanup();
      subs.hints.cleanup();
      subs.presetPill.cleanup();
      subs.tourStatus.cleanup();
      subs.hotspotDots.cleanup();
      subs.hotspotStrip.cleanup();
      subs.hotspotPopup.cleanup();
      subs.floorplanMinimap?.cleanup();
      subs.measurementOverlay.cleanup();
      subs.measurementPanel.cleanup();
      subs.walkHud.cleanup();
      subs.xrModal.cleanup();
      wrap.remove();
    },
  };
}
